import { React, useState, useEffect, useCallback } from "react";
import {
	List,
	ListItemText,
	ListItem,
	ListItemIcon,
	ListItemButton,
	Collapse,
	Box,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import "./CategoryList.scss";
import SubcategoryList from "./SubcategoryList";
import { useTranslation } from "react-i18next";
import ColorDisplayAndPicker from "../../../components/common/ColorDisplayAndPicker";

export default function CategoryList({
	categoryGroup,
	transactions,
	selectedCategory,
	setSelectedCategory,
	selectedSubcategory,
	setSelectedSubcategory,
}) {
	const [isExpanded, setIsExpanded] = useState(false);
	const isMobile = useMediaQuery("(max-width: 600px)");
	const theme = useTheme();
	const userCurrency = localStorage.getItem("userCurrency");
	const { t } = useTranslation();

	useEffect(() => {
		if (isMobile) {
			setIsExpanded(false);
		} else {
			setIsExpanded(false);
		}
	}, [isMobile]);

	const getTransactionsAmountForGroup = () => {
		const amountForGroup = transactions.reduce(
			(sum, item) =>
				item.includeInAnalysis
					? sum + item.amount * item.exchangeRate
					: sum,
			0
		);
		return parseFloat(amountForGroup.toFixed(2));
	};

	const getTransactionsForSubcategory = (subcategoryId) => {
		const transactionsForSubcategory = transactions.filter(
			(transaction) => transaction.subcategoryId === subcategoryId
		);
		return transactionsForSubcategory;
	};

	const handleExpandClick = (event, isExpanded) => {
		event.stopPropagation();
		setIsExpanded(isExpanded);
	};

	const handleSelectGroup = (event) => {
		event.stopPropagation();
		if (selectedCategory === categoryGroup) {
			setSelectedCategory(null);
			setSelectedSubcategory(null);
			setIsExpanded(false);
		} else {
			setSelectedCategory(categoryGroup);
			setSelectedSubcategory(null);
			setIsExpanded(true);
		}
	};

	const changeSelect = useCallback(() => {
		if (selectedCategory === null) {
			setIsExpanded(false);
		} else if (selectedCategory._id !== categoryGroup._id) {
			setIsExpanded(false);
		}
	}, [selectedCategory, categoryGroup, setIsExpanded]);
	useEffect(() => {
		changeSelect();
	}, [selectedCategory, changeSelect]);

	const mainBoxStyles = {
		flexGrow: 1,
		backgroundColor: theme.palette.background.paper,
		mx: 1,
		mb: 2,
		borderRadius: "10px",
	};

	const categoryGroupBoxStyles = {
		display: "flex",
		width: "100%",
		justifyContent: "space-between",
		alignItems: "center",
		textWrap: "nowrap",
	};

	return (
		<List sx={mainBoxStyles}>
			<ListItem sx={{ py: 0, px: 1 }} key={categoryGroup._id}>
				<ListItemButton
					sx={{
						py: 0.5,
						px: 1,
						borderRadius: "10px",
					}}
					onClick={(event) => handleSelectGroup(event)}
				>
					<Box sx={categoryGroupBoxStyles}>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<ColorDisplayAndPicker
								color={categoryGroup.color}
								onColorChange={() => {}}
								isEditable={false}
							/>

							<ListItemText
								primary={
									categoryGroup.groupName
										? categoryGroup.groupName
										: t(categoryGroup.groupTranslationKey)
								}
								primaryTypographyProps={{
									sx: {
										fontWeight: "bold",
										textAlign: "left",
									},
								}}
							/>
						</Box>
						<Box>
							<ListItemText
								primary={`${getTransactionsAmountForGroup().toFixed(2).toLocaleString()} ${userCurrency}`}
								primaryTypographyProps={{
									fontSize: "16px",
									ml: 2,
									fontWeight: "bold",
									textAlign: "right",
								}}
							/>
						</Box>
					</Box>
				</ListItemButton>

				{categoryGroup.subcategories.length !== 0 && (
					<ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
						{isExpanded ? (
							<ExpandLess
								sx={{ cursor: "pointer" }}
								onClick={(event) =>
									handleExpandClick(event, !isExpanded)
								}
							/>
						) : (
							<ExpandMore
								sx={{ cursor: "pointer" }}
								onClick={(event) =>
									handleExpandClick(event, !isExpanded)
								}
							/>
						)}
					</ListItemIcon>
				)}
			</ListItem>
			{categoryGroup.subcategories.length !== 0 && (
				<Collapse in={isExpanded}>
					<List
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
					>
						{categoryGroup.subcategories.map((subcategory) => (
							<SubcategoryList
								key={subcategory._id}
								category={categoryGroup}
								subcategory={subcategory}
								transactions={getTransactionsForSubcategory(
									subcategory._id
								)}
								setSelectedCategory={setSelectedCategory}
								selectedSubcategory={selectedSubcategory}
								setSelectedSubcategory={setSelectedSubcategory}
							/>
						))}
					</List>
				</Collapse>
			)}
		</List>
	);
}
